import React, { useState } from 'react';
import { PageWrapper } from '@components/layout/page-wrapper';
import { CaretDown } from '@phosphor-icons/react';
import { spacing } from '@config/spacing';
import {
  Container,
  Card,
  Title,
  Section,
  SectionTitle,
  Paragraph,
  OrderedList,
  StepNumber,
  Code,
  Link,
  DirectLinkBox,
  DirectLinkButton,
  ImportantNotice,
  FAQContainer,
  FAQItem,
  FAQQuestion,
  FAQAnswer,
  FAQAnswerInner,
} from './delete-data-page.styles';

/**
 * Delete Data Page
 * GDPR-compliant instructions for users to delete their data
 */
export const DeleteDataPage: React.FC = () => {
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null);

  const toggleFAQ = (id: string) => {
    setExpandedFAQ(expandedFAQ === id ? null : id);
  };

  return (
    <PageWrapper contained>
      <Container>
        <Card>
          <Title>Delete Your Data</Title>

          <ImportantNotice>
            <strong>This action is permanent and cannot be undone.</strong>
          </ImportantNotice>
          <ImportantNotice>
            <strong>
              If you have an active subscription, please cancel it before deleting your account
              to avoid future charges.
            </strong>{' '}
            Deleting your account does not automatically cancel subscriptions processed through
            Apple App Store or Google Play Store.
          </ImportantNotice>

          <Section>
            <SectionTitle>How to Delete Your Data</SectionTitle>
            <Paragraph>
              You can delete your account and all associated data directly from the app by
              following these steps:
            </Paragraph>

            <div style={{ marginLeft: spacing.xl }}>
              <Paragraph>
                <StepNumber>Step 1:</StepNumber> Sign in to your{' '}
                <Link
                  href="https://homeceo.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  HomeCEO account
                </Link>
              </Paragraph>
              <Paragraph>
                <StepNumber>Step 2:</StepNumber> Navigate to your Profile and scroll to the Data Management section
              </Paragraph>
              <Paragraph>
                <StepNumber>Step 3:</StepNumber> Click the{' '}
                <strong>&quot;Delete Account&quot;</strong> button
              </Paragraph>
              <Paragraph>
                <StepNumber>Step 4:</StepNumber> Confirm deletion by typing{' '}
                <Code>delete</Code> in the confirmation fields
              </Paragraph>
              <Paragraph>
                <StepNumber>Step 5:</StepNumber> Click &quot;Confirm Delete&quot; to permanently
                remove your account and all data
              </Paragraph>
            </div>

          </Section>

          <FAQContainer>
            <FAQItem>
              <FAQQuestion
                $expanded={expandedFAQ === 'what-deleted'}
                onClick={() => toggleFAQ('what-deleted')}
                aria-expanded={expandedFAQ === 'what-deleted'}
              >
                What Gets Deleted?
                <CaretDown weight="bold" />
              </FAQQuestion>
              <FAQAnswer $expanded={expandedFAQ === 'what-deleted'}>
                <FAQAnswerInner>
                  <Paragraph>
                    When you delete your account, the following data is permanently removed:
                  </Paragraph>
                  <OrderedList>
                    <li>Your account information including name and email address</li>
                    <li>Household data you have sole ownership of.</li>
                    <li>All research reports and saved analyses you have sole ownership of.</li>
                    <li>Your preferences and settings</li>
                    <li>
                      Shared data requires deletion of all HomeCEO data owner&apos;s accounts
                      before the data can be deleted.
                    </li>
                    <li>
                      On the Profile page, Export Your Data to download a copy of your data before
                      deleting your account. This will not be available after your account is
                      deleted.
                    </li>
                  </OrderedList>
                </FAQAnswerInner>
              </FAQAnswer>
            </FAQItem>

            <FAQItem>
              <FAQQuestion
                $expanded={expandedFAQ === 'need-help'}
                onClick={() => toggleFAQ('need-help')}
                aria-expanded={expandedFAQ === 'need-help'}
              >
                Need Help?
                <CaretDown weight="bold" />
              </FAQQuestion>
              <FAQAnswer $expanded={expandedFAQ === 'need-help'}>
                <FAQAnswerInner>
                  <Paragraph>
                    If you are unable to access your account or need assistance with data
                    deletion, please contact us and include your account email address.
                  </Paragraph>
                  <DirectLinkBox>
                    <DirectLinkButton href="/legal/contact-us">Contact Us</DirectLinkButton>
                  </DirectLinkBox>
                </FAQAnswerInner>
              </FAQAnswer>
            </FAQItem>
          </FAQContainer>
        </Card>
      </Container>
    </PageWrapper>
  );
};
